import {
  DataGrid,
  DataGridState,
  PageContainer,
  Typography,
} from '@sl/react-datafabric-components';
import { format, isValid } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';
import { useAgentContext } from '../../../../common/context/agent.provider';
import { capitalizeFirstLetter } from '../../../../common/utils/capitalize-first-letter';
import CreditsPage from '../components/credits/credits';
import { SubscriptionOptionsMenu } from './options-menu';
import { VerifyTokenPurchaseDialog } from './verify-buy-tokens-dialog';

export default function ManageSubscriptionPage() {
  const { agentSubscriptions, loadingSubscriptions, refetchSubscriptions } =
    useAgentContext();

  const [tableState, setTableState] = useState<DataGridState>({
    rows: 25,
    page: 1,
    first: 0,
    multiSort: [{ field: 'name', order: -1 }],
    filters: {
      queryString: '',
    },
  });

  const fullSubs = agentSubscriptions?.map((sub) => {
    const dateField = sub?.renewalDate ? 'renewalDate' : 'endDate';
    return { ...sub, dateField };
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const [isVerifyTokensDialogOpen, setIsVerifyTokensDialogOpen] =
    useState(false);
  const [checkoutTransactionId, setCheckoutTransactionId] = useState<
    string | null
  >(null);

  const memorizedTransaction = useMemo(() => {
    if (!checkoutTransactionId) return null; // Or some default/empty state if needed
    // sessionId is currently hardcoded as "", if it can change, include it in dependencies
    return { transactionId: checkoutTransactionId, sessionId: '' };
  }, [checkoutTransactionId]);

  // Effect to check URL params and open dialog - MOVED HERE
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Ensure it runs only in the browser
      const shouldVerifyTokens =
        searchParams.get('buy_tokens_return') === 'true';

      const transactionIdFromUrl = searchParams.get('transactionId');
      if (shouldVerifyTokens && transactionIdFromUrl) {
        setCheckoutTransactionId(transactionIdFromUrl);
        setIsVerifyTokensDialogOpen(true);

        // Clean the URL parameters immediately
        const newSearchParams = new URLSearchParams(searchParams.toString());
        newSearchParams.delete('buy_tokens_return');
        newSearchParams.delete('checkout_session_id');
        setSearchParams(newSearchParams, { replace: true });
      }
    }
  }, [
    searchParams,
    setSearchParams,
    setIsVerifyTokensDialogOpen,
    setCheckoutTransactionId,
  ]);

  return (
    <PageContainer>
      <div
        className='border-1 
      surface-border border-round-lg mt-6 mx-2 p-5'
      >
        <CreditsPage showRecentTransactions={false} />
      </div>
      {/* Dialog for verifying token purchase */}
      <VerifyTokenPurchaseDialog
        visible={isVerifyTokensDialogOpen}
        onHide={() => setIsVerifyTokensDialogOpen(false)}
        transaction={memorizedTransaction} // Use the memoized transaction
        onPurchaseComplete={refetchSubscriptions} // Refetch data after purchase is finalized (success or error)
      />
      <Outlet />
      <DataGrid
        dataKey='id'
        entityLabel='Subscriptions'
        header={
          <Typography type={2} className={'text-3xl font-bold mb-6'}>
            Subscriptions
          </Typography>
        }
        value={loadingSubscriptions ? undefined : fullSubs}
        gridState={tableState}
        onFiltersUpdate={setTableState}
        loading={loadingSubscriptions}
        columns={[
          {
            field: 'plan.name',
            header: 'Plan',
            body: (rowData) =>
              rowData.plan?.name
                ? capitalizeFirstLetter(rowData.plan.name)
                : '',
          },
          {
            field: 'currentTokens',
            header: 'Credits',
            body: (rowData) =>
              rowData.currentTokens ? rowData.currentTokens.toFixed(2) : 'N/A',
          },

          {
            field: 'dateField',
            header: 'End Date / Renewal Date',
            sortable: true,
            body: (rowData) =>
              rowData[rowData.dateField] &&
              isValid(new Date(rowData[rowData.dateField]))
                ? format(new Date(rowData[rowData.dateField]), 'dd-MM-yyyy')
                : '',
          },
          {
            field: 'status',
            header: 'Status',
            type: 'status',
            readOnly: true,
            statusOptions: [
              {
                label: 'Active',
                value: 'active',
                severity: 'success',
              },
              {
                label: 'Blocked',
                value: 'blocked',
                severity: 'danger',
              },
              {
                label: 'Canceled',
                value: 'canceled',
                color: 'dimgray',
                textColor: 'white',
              },
              {
                label: 'Payment Failed',
                value: 'paymentFailed',
                severity: 'warning',
              },
            ],
          },
          {
            field: '',
            body: (rowData: any) => (
              <div className='flex flex-0 justify-content-end align-items-center'>
                <SubscriptionOptionsMenu rowData={rowData} />
              </div>
            ),
          },
        ]}
      />
      {/* </div> */}
    </PageContainer>
  );
}
